import jsPDF from "jspdf";

/**
 * Generates a print-and-fold 8.5×11" PDF with TWO table tents per sheet.
 *
 * Layout (US letter portrait):
 *   ┌─────────────┬─────────────┐
 *   │             │             │
 *   │  TENT 1     │  TENT 2     │ ← front faces (top half of each strip)
 *   │  FRONT      │  FRONT      │
 *   │             │             │
 *   ├─ fold ──────┼─ fold ──────┤ y = 5.5"
 *   │             │             │
 *   │  TENT 1     │  TENT 2     │ ← back faces (printed UPSIDE DOWN so they
 *   │  BACK 180°  │  BACK 180°  │   read correctly when the strip is folded)
 *   │             │             │
 *   └─────────────┴─────────────┘
 *               ↑ cut line at x = 4.25"
 *
 * User instructions (printed faintly along the cut/fold lines):
 *   1. Cut down the center vertical line
 *   2. Fold each strip in half along the dashed horizontal line
 *   3. Stand on the table
 *
 * @param {Object}  cafe              { name, slug, custom_note }
 * @param {string}  qrCanvasSelector  CSS selector for the visible QR <canvas> wrapper
 * @param {string}  landingOrigin     window.location.origin (or canonical drinksomalixir.com)
 * @param {string}  variant           "classic" | "poetic" | "close"
 * @param {boolean} lightMode         true = cream paper + navy ink (ink-saver, default)
 */
export async function generateTableTentPDF({
  cafe,
  qrCanvasSelector,
  landingOrigin,
  variant = "classic",
  lightMode = true,
}) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "in", format: "letter", compress: true });

  const PAGE_W = 8.5;
  const PAGE_H = 11;
  const CUT_X = PAGE_W / 2;
  const FOLD_Y = PAGE_H / 2;
  const FACE_W = PAGE_W / 2; // 4.25"
  const FACE_H = PAGE_H / 2; // 5.5"

  const palette = lightMode
    ? { bg: [244, 236, 216], ink: [11, 20, 42], muted: [58, 80, 110], accent: [58, 124, 165], border: [11, 20, 42], guide: [180, 170, 145] }
    : { bg: [11, 20, 42],    ink: [232, 238, 245], muted: [200, 212, 224], accent: [143, 196, 224], border: [143, 196, 224], guide: [80, 100, 130] };

  // ---------- background fill (entire sheet) ----------
  pdf.setFillColor(...palette.bg);
  pdf.rect(0, 0, PAGE_W, PAGE_H, "F");

  // ---------- pull QR canvas as image (matches selected mode) ----------
  const canvas = document.querySelector(`${qrCanvasSelector} canvas`);
  if (!canvas) throw new Error("QR canvas not found. Make sure the QR is rendered on the page.");
  const hiRes = document.createElement("canvas");
  hiRes.width = 900;
  hiRes.height = 900;
  const hctx = hiRes.getContext("2d");
  hctx.fillStyle = lightMode ? "#F4ECD8" : "#0B142A";
  hctx.fillRect(0, 0, 900, 900);
  hctx.imageSmoothingEnabled = false;
  hctx.drawImage(canvas, 0, 0, 900, 900);
  const qrDataUrl = hiRes.toDataURL("image/png");

  // ---------- draw the four tent faces ----------
  // top-left front face
  drawFace(pdf, 0,     0,        FACE_W, FACE_H, palette, qrDataUrl, cafe, variant, landingOrigin, false);
  // top-right front face
  drawFace(pdf, CUT_X, 0,        FACE_W, FACE_H, palette, qrDataUrl, cafe, variant, landingOrigin, false);
  // bottom-left BACK face (upside down)
  drawFace(pdf, 0,     FOLD_Y,   FACE_W, FACE_H, palette, qrDataUrl, cafe, variant, landingOrigin, true);
  // bottom-right BACK face (upside down)
  drawFace(pdf, CUT_X, FOLD_Y,   FACE_W, FACE_H, palette, qrDataUrl, cafe, variant, landingOrigin, true);

  // ---------- guide lines ----------
  // vertical CUT line (solid, with scissor symbol)
  pdf.setDrawColor(...palette.guide);
  pdf.setLineWidth(0.008);
  pdf.line(CUT_X, 0.15, CUT_X, PAGE_H - 0.15);
  // horizontal FOLD lines (dashed) — one in top tent strip, one in bottom strip
  // Actually each STRIP folds in half — so the fold is the page midline FOLD_Y.
  // We mark it as a dashed line.
  pdf.setLineDashPattern([0.05, 0.05], 0);
  pdf.line(0.15,         FOLD_Y, CUT_X - 0.05, FOLD_Y);
  pdf.line(CUT_X + 0.05, FOLD_Y, PAGE_W - 0.15, FOLD_Y);
  pdf.setLineDashPattern([], 0); // reset

  // tiny scissor symbol on the cut line
  pdf.setFont("courier", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...palette.guide);
  pdf.text("✂", CUT_X, 0.18, { align: "center" });
  pdf.text("✂", CUT_X, PAGE_H - 0.10, { align: "center" });
  // "fold" label on the fold line
  pdf.setFontSize(6);
  pdf.text("FOLD", CUT_X - 0.05 - 0.18, FOLD_Y + 0.025, { align: "right" });
  pdf.text("FOLD", CUT_X + 0.05 + 0.18, FOLD_Y + 0.025, { align: "left" });
  // "CUT" label near the very top of the cut line
  pdf.text("CUT", CUT_X + 0.04, 0.36, { align: "left" });

  // ---------- instructions strip (very bottom, between cut marks, faint) ----------
  pdf.setFont("courier", "normal");
  pdf.setFontSize(6.5);
  pdf.setTextColor(...palette.guide);
  // We use the literal sequence "cut down the middle, then fold each strip in half" — they print along the bottom edge in tiny type
  // (positioned outside the face content; faces' bottom margin already leaves room)
  // Actually for cleanliness, we skip the bottom strip — fold/cut labels above already do the job.

  pdf.save(`soma-lixir-${cafe.slug}-table-tents-${variant}${lightMode ? "-light" : "-dark"}.pdf`);
}

// =====================================================================
// One full table-tent face — drawn within the given (x, y, w, h) rectangle.
// If `flip` is true, all content is rendered upside-down (180°) AND mirrored
// vertically within the rect, so the strip reads correctly when folded.
// =====================================================================
function drawFace(pdf, x, y, w, h, palette, qrDataUrl, cafe, variant, landingOrigin, flip) {
  // Inner padding so content doesn't crowd the cut/fold edges
  const PAD_OUTER = 0.30;
  const PAD_FOLD  = 0.55;
  const PAD_SIDE  = 0.30;

  // For each "relative Y from the outer edge", returns the absolute Y on the page.
  // - Front face (flip=false): outer edge is the TOP of this rect, so absY = y + relY
  // - Back face  (flip=true):  outer edge is the BOTTOM of this rect, so absY = y + h - relY
  const placeY = (relY) => (flip ? y + h - relY : y + relY);
  // For text/image rendered at a CENTER point, when flipped we add an angle of 180°
  const angleOpt = flip ? { angle: 180 } : {};
  const faceCenterX = x + w / 2;

  const cafeNameUpper = (cafe.name || "Welcome").toUpperCase();
  const slugUrl = `${landingOrigin.replace(/^https?:\/\//, "")}/c/${cafe.slug}`;

  // Available content rectangle (relative to outer edge)
  const innerW = w - PAD_SIDE * 2;
  const innerH = h - PAD_OUTER - PAD_FOLD;

  // === Header
  pdf.setTextColor(...palette.ink);
  let relY = PAD_OUTER + 0.22;

  if (variant === "poetic") {
    pdf.setFont("times", "italic");
    pdf.setFontSize(15);
    const head = pdf.splitTextToSize('"Your cup is empty. What remains is not."', innerW);
    head.forEach((line, i) => {
      pdf.text(line, faceCenterX, placeY(relY + i * 0.20), { align: "center", ...angleOpt });
    });
    relY += 0.20 * head.length + 0.05;
  } else {
    pdf.setFont("times", "italic");
    pdf.setFontSize(17);
    pdf.text("A SIREN'S READING", faceCenterX, placeY(relY), { align: "center", ...angleOpt });
    relY += 0.30;
    if (variant === "close") {
      pdf.setFont("courier", "normal");
      pdf.setFontSize(6);
      pdf.setTextColor(...palette.accent);
      pdf.text("⌁  TASSEOGRAPHY  ·  COFFEE GROUNDS  ⌁", faceCenterX, placeY(relY), { align: "center", ...angleOpt });
      relY += 0.18;
      pdf.setTextColor(...palette.ink);
    }
  }

  // === QR (centered horizontally)
  const qrSize = Math.min(1.9, innerW - 0.4);
  // The image's anchor in jsPDF is its top-left corner — so the top-left when flipped
  // is the BOTTOM-RIGHT after rotation. We pass the rotation argument to addImage.
  const qrX = faceCenterX - qrSize / 2;
  const qrTopRel = relY + 0.05;
  const qrTopAbs = flip ? (y + h - qrTopRel - qrSize) : (y + qrTopRel);
  if (flip) {
    // jsPDF addImage rotation: positive = counter-clockwise. 180° rotates around top-left;
    // to keep the image in its visual rectangle, we rotate around the image center,
    // which is achieved by drawing at the SAME top-left but with rotation=180 — jsPDF
    // handles this internally by rotating about the image's own corner. The simplest
    // way to keep the image inside our intended bounding box is to draw the image at
    // the BOTTOM-RIGHT corner then rotate -180 (or +180), which lands the rotated
    // image where we wanted. The math here uses (qrX + qrSize, qrTopAbs + qrSize):
    pdf.addImage(qrDataUrl, "PNG", qrX + qrSize, qrTopAbs + qrSize, qrSize, qrSize, undefined, "FAST", 180);
  } else {
    pdf.addImage(qrDataUrl, "PNG", qrX, qrTopAbs, qrSize, qrSize, undefined, "FAST", 0);
  }
  relY = qrTopRel + qrSize + 0.22;

  // === CTA + body (variant-specific)
  pdf.setTextColor(...palette.ink);
  if (variant === "classic") {
    pdf.setFont("times", "italic");
    pdf.setFontSize(11);
    pdf.text("Scan your cup. Get a reading.", faceCenterX, placeY(relY), { align: "center", ...angleOpt });
    relY += 0.17;
    pdf.setFont("times", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...palette.muted);
    const body = pdf.splitTextToSize(
      "Three minutes. No download. Just your dregs and whatever they decided to tell you.",
      innerW
    );
    body.forEach((line, i) => {
      pdf.text(line, faceCenterX, placeY(relY + i * 0.13), { align: "center", ...angleOpt });
    });
    relY += 0.13 * body.length + 0.05;
  } else if (variant === "poetic") {
    pdf.setFont("times", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...palette.muted);
    const body = pdf.splitTextToSize(
      "Scan for a three-minute reading. No app. No sign-up. Just the traces your coffee left behind, and what they know about you.",
      innerW
    );
    body.forEach((line, i) => {
      pdf.text(line, faceCenterX, placeY(relY + i * 0.13), { align: "center", ...angleOpt });
    });
    relY += 0.13 * body.length + 0.05;
  } else { // close
    pdf.setFont("times", "italic");
    pdf.setFontSize(11);
    pdf.text('"Finish your drink. Then look closer."', faceCenterX, placeY(relY), { align: "center", ...angleOpt });
    relY += 0.17;
    pdf.setFont("times", "normal");
    pdf.setFontSize(8.5);
    pdf.setTextColor(...palette.muted);
    const body = pdf.splitTextToSize(
      "The grounds hold echoes. The silt knows. Scan for a three-minute reading, no app, no trace.",
      innerW
    );
    body.forEach((line, i) => {
      pdf.text(line, faceCenterX, placeY(relY + i * 0.13), { align: "center", ...angleOpt });
    });
    relY += 0.13 * body.length + 0.05;
  }

  // === SBWU footer + signature — anchored relative to the FOLD edge
  // We compute positions as "distance from fold edge" so they sit just inside the fold padding.
  const distFromFold = (d) => placeY(h - PAD_FOLD + d); // d=0 sits at the fold-edge inside boundary
  // Actually we want to render *above* the fold edge inside the face, so we use distances measured
  // back from the fold padding line.

  // separator line — sits about 0.45" from the fold-edge inside boundary, on the outer side
  const sepRel = h - PAD_FOLD - 0.45;
  const sepAbsY = placeY(sepRel);
  pdf.setDrawColor(...palette.accent);
  pdf.setLineWidth(0.006);
  pdf.line(x + PAD_SIDE + 0.3, sepAbsY, x + w - PAD_SIDE - 0.3, sepAbsY);

  pdf.setFont("times", "normal");
  pdf.setFontSize(7.5);
  pdf.setTextColor(...palette.ink);
  const fLine1 = pdf.splitTextToSize(`Every reading at ${cafeNameUpper} supports Starbucks Workers United.`, innerW);
  fLine1.forEach((line, i) => {
    pdf.text(line, faceCenterX, placeY(sepRel + 0.16 + i * 0.12), { align: "center", ...angleOpt });
  });

  pdf.setFontSize(7);
  pdf.setTextColor(...palette.muted);
  const fLine2 = pdf.splitTextToSize("20% of proceeds go directly to the baristas still fighting for fair contracts.", innerW);
  fLine2.forEach((line, i) => {
    pdf.text(line, faceCenterX, placeY(sepRel + 0.30 + i * 0.12), { align: "center", ...angleOpt });
  });

  pdf.setFont("courier", "normal");
  pdf.setFontSize(6);
  pdf.setTextColor(...palette.muted);
  // signature sits just above the fold padding
  pdf.text(`soma lixir  •  ${slugUrl}`, faceCenterX, placeY(h - PAD_FOLD - 0.04), { align: "center", ...angleOpt });
}
